import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircleOutline, ErrorOutline } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { useForgotPassword } from '@/data/auth';
import { session_path } from '@/utils/routes';

const loginValidationSchema = z.object({
  access: z.string().min(1, 'Digite seu e-mail ou CPF'),
});

type LoginValidationData = z.infer<typeof loginValidationSchema>;

export type AccessDataProps = {
  nextStep: (login: string) => void;
};

export const AccessData = () => {
  const navigate = useNavigate();
  const [messageSuccess, setMessageSuccess] = useState<string | null>(null);
  const { mutate: resetPassword, isPending, isSuccess } = useForgotPassword();

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: { access: '' },
    resolver: zodResolver(loginValidationSchema),
  });

  const onSubmit: SubmitHandler<LoginValidationData> = (event) => {
    resetPassword(event.access, {
      onSuccess: (response) => {
        setMessageSuccess(response.message);
      },
      onError: (error) => {
        setError('access', {
          type: 'server',
          message:
            error.message ?? 'Erro ao enviar link de recuperação de senha',
        });
      },
    });
  };

  const handleGoToPageLogin = () => navigate(session_path());

  return (
    <Stack gap={3}>
      <Stack gap={1}>
        <Typography variant="h2" id="forgot-password-label">
          Esqueceu sua senha?
        </Typography>
        <Typography sx={{ whiteSpace: 'pre-line' }}>
          Vamos enviar um link para você criar uma nova senha.
        </Typography>
      </Stack>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={2}>
          <Controller
            name="access"
            control={control}
            render={({ field }) => (
              <TextField
                id="access"
                placeholder="Digite seu e-mail ou CPF"
                variant="outlined"
                error={Boolean(errors.access)}
                fullWidth
                helperText={
                  errors?.access?.message ?? (isSuccess && messageSuccess)
                }
                sx={{
                  '& .MuiFormHelperText-root': {
                    color: isSuccess && messageSuccess ? 'green' : 'red',
                  },
                }}
                {...field}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {errors.access ? (
                          <IconButton edge="end">
                            <ErrorOutline color="error" />
                          </IconButton>
                        ) : isSuccess && messageSuccess ? (
                          <IconButton edge="end">
                            <CheckCircleOutline color="success" />
                          </IconButton>
                        ) : null}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />

          {isSuccess && (
            <Button variant="contained" onClick={handleGoToPageLogin}>
              Voltar para login
            </Button>
          )}

          {!isSuccess && (
            <Button
              loading={isPending}
              loadingPosition="start"
              startIcon={isPending && <CircularProgress size={24} />}
              type="submit"
              variant="contained"
            >
              Enviar
            </Button>
          )}
        </Stack>
      </form>
    </Stack>
  );
};
